/**
 * runtimeOverlay.ts — runtime-state types + non-layout-touching helpers
 * lifted from chatDagDataProcessor at Phase 2 (T2.1).
 *
 * Phase 4 cutover: the clean messagesToDag.ts imports from here.
 * chatDagDataProcessor.ts re-exports through Phase 4 for compat,
 * then gets deleted.
 *
 * No semantic changes from the lift. Exports:
 *   RuntimeState type + EMPTY_RUNTIME
 *   coerceContentToString, toolCallId, toolName, unwrapResult
 *   fanCollapsed, phaseStackCollapsed, deriveStackName, isLatestTurnByIndex
 *   _emitArtifactTilesAndOrphans, orphanRunningSweep, errorTurnSweep
 *   emitSpeculativeTurn
 */
import type { Node, Edge } from 'reactflow';
import type { Message } from '../../../../contexts/ConversationContext';
import type { ToolCallEntry } from '../../ToolCallPill';
import type { DagResponse } from '../../../../types/dag';
import type {
  ChatDagNodeData,
  CollapsedPhaseStackData,
  LlmRoundData,
  PeerDagData,
  ToolCallDagData,
  TurnMarkerData,
  GroupingDim,
  ArtifactContentEntry,
} from '../types';
import { expandedTileSize } from '../chatDagTokens';

// LLM `content` field comes through in three shapes (openrouter
// normalizes but edge cases slip through): plain string (standard),
// null (tool-only reply with no text), or a content-block list
// (Anthropic cache_control path). Coerce to a safe string so
// downstream renderers can rely on `.slice` / `.split` without
// TypeErrors. Mirrors the backend's TokenCounterService._content_to_text
// helper.
export function coerceContentToString(value: any): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  if (Array.isArray(value)) {
    // content-block list: concatenate the `text` of text-typed blocks
    return value
      .map((block) => {
        if (typeof block === 'string') return block;
        if (block && typeof block === 'object') {
          if (typeof block.text === 'string') return block.text;
          if (typeof block.content === 'string') return block.content;
        }
        return '';
      })
      .join('');
  }
  if (typeof value === 'object') {
    // Backend truncation stub from `structured_truncate` —
    // {__truncated: true, bytes: N, sha256_prefix8: '...'}. Pre-2026-04-25
    // turns hit a 512B leaf cap, clipping peer responses + prompts into
    // these stubs. Render a placeholder so ChatMap nodes show *something*
    // instead of empty bubbles.
    if (value.__truncated === true) {
      const bytes: number | undefined = typeof value.bytes === 'number' ? value.bytes : undefined;
      const sizeStr =
        bytes == null
          ? ''
          : bytes < 1024
            ? `${bytes}B`
            : bytes < 1024 * 1024
              ? `${(bytes / 1024).toFixed(1)}KB`
              : `${(bytes / 1024 / 1024).toFixed(1)}MB`;
      return `[truncated by older log policy${sizeStr ? ` · ${sizeStr}` : ''}]`;
    }
    const inner = value.text ?? value.content ?? value.response;
    if (typeof inner === 'string') return inner;
  }
  return '';
}

export function toolCallId(call: ToolCallEntry, fallback: string): string {
  return call.identity?.tool_call_id ?? call.tool_call_id ?? fallback;
}

export function toolName(call: ToolCallEntry): string {
  return call.identity?.tool_name ?? call.tool_name ?? '';
}

export function unwrapResult(call: ToolCallEntry): any {
  const r = call.result;
  if (r && typeof r === 'object' && 'result' in (r as any)) {
    return (r as any).result;
  }
  return r;
}

// Default-expanded density rule (Alan's v0.3 polish ask): fan-outs
// stay fanned out until the user explicitly collapses them. A fan is
// collapsed iff its parent tool_call_id is present in collapsedFanIds.
export function fanCollapsed(
  collapsedFanIds: ReadonlySet<string>,
  toolCallId: string,
): boolean {
  return collapsedFanIds.has(toolCallId);
}

export interface RuntimeState {
  inFlightTurnId: string | null;
  viewportTurnId: string | null;
  // Default-expanded: set contains parent tool_call_ids that the user
  // has opted to fold into a chip stand-in.
  collapsedFanIds: ReadonlySet<string>;
  livePhaseEvents: any[];       // PhaseEvent[] — loosened here to keep processor pure
  wsLastEventMs: number | null;
  // The user message id of a turn that failed (state.status === 'error')
  // so the error-turn sweep can flip orphan runners to 'error' and emit
  // a synthetic assistant marker.
  errorTurnId?: string | null;
  // Controls-panel global override: when true, EVERY consult_peers
  // fan is forced to chip (card-deck) rendering regardless of
  // per-tool state in collapsedFanIds.
  allFansCollapsed?: boolean;
  // Phase-stack collapse state: each turnKey (user message id) in
  // this set has its rounds/tools/peers replaced by a single
  // collapsed_phase_stack card. Feeds the per-turn toggles in the
  // controls panel.
  collapsedPhaseStackIds?: ReadonlySet<string>;
  // Global override: when true, EVERY phase stack is collapsed.
  allPhaseStacksCollapsed?: boolean;
  // Trailmap L2 Drip 1 — per-turn artifact disclosure: each turnKey
  // (source_turn_id / user msg id) in the set has its artifact_tile
  // nodes revealed; absent turns show only the count badge. UNDEFINED
  // means "show all" (backward-compat for existing callers/tests).
  expandedArtifactTurnIds?: ReadonlySet<string>;
  // Trailmap L2 Drip 2 — active grouping dim (off = flat / Drip 1). Threaded
  // into Pass 1 (emit-bypass) + Pass 2 (clustering geometry).
  activeDim?: GroupingDim;
  // Trailmap L2 Drip 3 — per-artifact expansion set (which tiles are blown
  // open to content). Threaded into the tile emit (Pass 1) to set explicit
  // expanded width/height + carry the fetched content for reflow.
  expandedArtifactIds?: ReadonlySet<string>;
  // v0.4 Task 6: primary persona record for this chat. Attached
  // to assistant turn_marker data so rich variants can render the
  // full PersonaCardBody without each node fetching independently.
  primaryPersona?: any;
  // Living Blueprint SP1 — the manifest id bound to this trail (fetched by
  // ChatDagCanvas via GET /artifactory/session/{id}/blueprint, since the
  // chat-session API doesn't serialize session.data). Drives the blueprint
  // flip-card emit. onOpenBlueprint routes to the full Artifactory view.
  blueprintManifestId?: string | null;
  onOpenBlueprint?: (id: string) => void;
  // SP2b — open the genesis cost-gate for a blueprint card (seedling Generate /
  // failure Retry). Threaded onto the emitted blueprint node's data.onGenerate.
  onGenerateBlueprint?: (manifestId: string, intent: string) => void;
  // SP-3b — chat-with-artifact spawn channels (from useSpineSpawnControl). The
  // tile node receives these as typed callbacks (never touches runtime directly).
  onAskArtifact?: (artifactId: string) => void;
  askInFlight?: boolean;
  askError?: string | null;
  onSeedSpineExpanded?: (cardId: string) => void;
  onToggleSpine?: (cardId: string) => void;
}

export const EMPTY_RUNTIME: RuntimeState = {
  inFlightTurnId: null,
  viewportTurnId: null,
  collapsedFanIds: new Set(),
  livePhaseEvents: [],
  wsLastEventMs: null,
  errorTurnId: null,
  allFansCollapsed: false,
  collapsedPhaseStackIds: new Set(),
  allPhaseStacksCollapsed: false,
  expandedArtifactTurnIds: new Set(),
  activeDim: 'turn', // flat ('off') removed — recon 2026-06-14
  expandedArtifactIds: new Set(),
  primaryPersona: null,
  blueprintManifestId: null,
  onOpenBlueprint: undefined,
  onGenerateBlueprint: undefined,
  onAskArtifact: undefined,
  askInFlight: false,
  askError: null,
  onSeedSpineExpanded: undefined,
  onToggleSpine: undefined,
};

// True iff this turn's phase stack should render as a single
// collapsed card instead of the full rounds/tools/peers.
export function phaseStackCollapsed(
  runtime: RuntimeState,
  turnKey: string,
): boolean {
  if (runtime.allPhaseStacksCollapsed) return true;
  return !!runtime.collapsedPhaseStackIds?.has(turnKey);
}

// Derive a human-readable name for a phase stack from the user
// prompt. Short enough to fit in both the box label + the controls
// list. Whitespace-collapsed so newlines don't blow up single-line
// truncation on rendering. Leading markdown noise (#, *, _, >, `,
// list bullets, etc.) is stripped so a `# Brainstorm session`
// prompt doesn't display its literal hash on the stack label.
export function deriveStackName(userContent: string | undefined): string {
  const collapsed = (userContent ?? '').replace(/\s+/g, ' ').trim();
  // Strip leading markdown chrome — heading hashes, blockquote `>`,
  // list bullets `-`/`*`/`+`, surrounding emphasis asterisks/
  // underscores, leading inline-code backticks. Operates only on
  // the leading run; mid-string markdown stays as-is (we render
  // labels as plain text downstream).
  const raw = collapsed.replace(/^[\s>#*_`+\-]+\s*/, '').trim() || collapsed;
  if (!raw) return '(empty prompt)';
  const MAX = 48;
  return raw.length <= MAX ? raw : raw.slice(0, MAX - 1) + '…';
}

export function isLatestTurnByIndex(
  turnIndex: number,
  maxIndex: number,
  inFlightTurnId: string | null,
  userMessageId: string,
): boolean {
  if (inFlightTurnId != null) {
    return inFlightTurnId === userMessageId;
  }
  return turnIndex === maxIndex;
}




export function _emitArtifactTilesAndOrphans(
  nodes: Node[],
  edges: Edge[],
  dag: DagResponse,
  messages: Message[],
  onTileClick?: (artifactId: string) => void,
  // Trailmap L2 Drip 1 — when provided, only turns whose source_turn_id
  // is in the set emit tiles; others show count only. UNDEFINED = show all.
  expandedArtifactTurnIds?: ReadonlySet<string>,
  // Trailmap L2 Drip 2 — grouped mode emits ALL tiles (bypasses the collapse
  // gate); cross-turn dims (phase/persona) drop the per-turn edge.
  activeDim?: GroupingDim,
  // Trailmap L2 Drip 3 — tiles in this set emit at their expanded footprint
  // (expandedTileSize by type) and carry the fetched body from artifactContent.
  expandedArtifactIds?: ReadonlySet<string>,
  artifactContent?: ReadonlyMap<string, ArtifactContentEntry>,
  // SP-3b — chat-with-artifact spawn channels stamped onto each tile's data
  // (mirrors how onTileClick reaches the tile). The tile never reads runtime.
  spawnControls?: {
    onAsk?: (artifactId: string) => void;
    askInFlight?: boolean;
    askError?: string | null;
    onSeedSpineExpanded?: (cardId: string) => void;
    onToggleSpine?: (cardId: string) => void;
  },
): void {
  const click = onTileClick ?? (() => {});

  // L1+ Fix 2 (2026-05-12) — Defensive: artifacts with non-NULL
  // source_turn_id whose parent turn-marker can't be resolved are
  // STALE-bound (envelope/message_id drift, truncated viewport, etc).
  // Route them into orphan_tray instead of dropping silently, so the
  // operator can still see + open them. DC3 spirit: visible-not-invisible.
  const stalelyBoundArtifacts: typeof dag.artifacts = [];

  // Bound artifacts → one tile node per artifact (when parent resolves).
  for (const artifact of dag.artifacts) {
    if (!artifact.source_turn_id) continue;

    // L1+ (2026-05-12, per Alan) — cluster artifacts around the OUTPUT
    // node (assistant msg) of a turn, not the input (user msg). The
    // DB binding semantic stays accurate (source_turn_id = the user
    // prompt that drove the artifact). The frontend translates user
    // msg id → corresponding assistant turn-marker for visual
    // placement.
    //
    // Resolution order:
    //   1. Find the user-message index for source_turn_id
    //   2. If the next message is the assistant response, bind to
    //      asst-${that.id} turn-marker (the OUTPUT node)
    //   3. Fallback: bind to user-${source_turn_id} turn-marker (e.g.
    //      assistant hasn't landed yet mid-turn)
    //   4. If neither resolves → Fix 2 path (orphan tray)
    let parentNode: Node | undefined;

    const userMsgIdx = messages.findIndex(
      (m) => m.id === artifact.source_turn_id,
    );
    if (userMsgIdx >= 0 && userMsgIdx + 1 < messages.length) {
      const nextMsg = messages[userMsgIdx + 1];
      if (nextMsg.role === 'assistant') {
        parentNode = nodes.find(
          (n) =>
            n.type === 'turn_marker' &&
            (n.data as any)?.messageId === nextMsg.id,
        );
      }
    }

    if (!parentNode) {
      // Fallback to user turn-marker (assistant not yet landed).
      // Per Front F1 v1.1: turn nodes use prefix `user-${id}`/`asst-${id}`
      // — never raw msg.id.
      parentNode = nodes.find(
        (n) =>
          n.type === 'turn_marker' &&
          (n.data as any)?.messageId === artifact.source_turn_id,
      );
    }

    if (!parentNode) {
      // L1+ Fix 2: stale source_turn_id → fall through to orphan tray
      // instead of dropping. Operator sees the artifact + can click to
      // open. Future: surface this as a distinct "Stale Binding" group
      // when the artifact-groupings arc lands.
      stalelyBoundArtifacts.push(artifact);
      continue;
    }

    // Drip 1: stamp the count + toggle key on the parent marker (ALWAYS,
    // regardless of collapse) so the badge is truthful (DC-1).
    const pdata = parentNode.data as any;
    pdata.artifactCount = (pdata.artifactCount ?? 0) + 1;
    pdata.artifactTurnKey = artifact.source_turn_id;
    pdata.artifactsExpanded = expandedArtifactTurnIds
      ? expandedArtifactTurnIds.has(artifact.source_turn_id)
      : true;

    // Drip 2: in grouped mode (activeDim !== off) emit ALL tiles — the grouping
    // IS the disclosure. Flat mode keeps Drip 1's collapse gate (count stamped above).
    const grouped = !!activeDim && activeDim !== 'off';
    if (!grouped && expandedArtifactTurnIds && !expandedArtifactTurnIds.has(artifact.source_turn_id)) {
      continue;
    }

    const tileId = `tile-${artifact.id}`;
    // Trailmap L2 Drip 3 — expanded tiles carry an explicit content-natural
    // footprint (by type) so the height-aware clustering path reflows around
    // them; collapsed tiles keep the 320x80 glyph card.
    const isExpanded = !!expandedArtifactIds?.has(artifact.id);
    const sz = isExpanded ? expandedTileSize(artifact.type) : { w: 320, h: 80 };
    const entry = isExpanded ? artifactContent?.get(artifact.id) : undefined;
    nodes.push({
      id: tileId,
      type: 'artifact_tile',
      position: { x: 0, y: 0 }, // layout reassigns in chatDagLayout (Task 14)
      // Explicit dims (DIM_FALLBACK artifact_tile) — RF11 fitView returns
      // FALSE for the WHOLE canvas while ANY node is unmeasured, and a
      // culled (never-rendered) tile never measures → every turn-end widen
      // no-ops forever (field 2026-06-12; reactflow gotcha #4 sweep missed
      // this push site). Expanded tiles keep dims explicit too (just larger).
      width: sz.w,
      height: sz.h,
      data: {
        kind: 'artifact_tile',
        artifact,
        onClick: click,
        expanded: isExpanded,
        content: entry?.status === 'ok' ? entry.artifact : undefined,
        contentError: entry?.status === 'error',
        // SP-3b — Ask affordance (C3) + F5 operator-seed (C6) callbacks.
        onAsk: spawnControls?.onAsk,
        askInFlight: spawnControls?.askInFlight,
        askError: spawnControls?.askError,
        onSeedSpineExpanded: spawnControls?.onSeedSpineExpanded,
        onToggleSpine: spawnControls?.onToggleSpine,
      },
    } as Node);

    // Drip 2: cross-turn dims (phase/persona) relocate tiles away from their
    // turn-marker, so the per-turn fan edge no longer makes sense — drop it.
    // Flat + the turn dim keep the edge (lean AABB-only; L2 §4.5).
    if (!grouped || activeDim === 'turn') {
      edges.push({
        id: `edge-tile-${artifact.id}`,
        source: parentNode.id,  // 'user-<msg-id>' or 'asst-<msg-id>'
        target: tileId,
        // L1+ (2026-05-12 per Alan): edges exit the turn-marker from its
        // RIGHT side (sourceHandle 'r' — added on TurnMarkerNode{Compact,Rich}
        // 2026-05-12) and enter the tile from its LEFT side (existing 'l'
        // target Handle on ArtifactTileNode). Visual: clean horizontal fan
        // from the turn into its artifact column.
        sourceHandle: 'r',
        targetHandle: 'l',
        type: 'default',
        style: { strokeDasharray: '4 4', opacity: 0.6 },
      } as Edge);
    }
  }

  // L1+ Fix 2: merge backend-marked orphans (NULL source_turn_id) with
  // stale-bound artifacts (source_turn_id set but parent unrendered) so
  // they all surface in the Orphan Tray. Dedup by id in case backend
  // already counted a stale one as orphan.
  const orphanIds = new Set(dag.orphan_artifacts.map((a) => a.id));
  const mergedOrphans = [
    ...dag.orphan_artifacts,
    ...stalelyBoundArtifacts.filter((a) => !orphanIds.has(a.id)),
    // SP-3c Slice B — blueprint-internal files (agent_id artifactory_<node_id>) are
    // represented by their blueprint's in-card ▣ N cache disclosure, not as loose
    // orphan tiles. Suppress them here so the tray only shows genuine orphans.
    // Note: orphan_artifacts is already session-scoped, and within a session the bp
    // card is on canvas; the rare cross-session/culled-card case means those files
    // are reachable only via the Artifactory surface — acceptable, not lost.
  ].filter((a) => !String((a as any).agent_id ?? '').startsWith('artifactory_'));

  // Orphan tray — one node holding all orphans + stalely-bound (DC3).
  if (mergedOrphans.length > 0) {
    const trayId = 'orphan-tray';
    nodes.push({
      id: trayId,
      type: 'orphan_tray',
      position: { x: 0, y: 0 },
      // Explicit dims (DIM_FALLBACK orphan_tray) — same fitView-blocking
      // class as the artifact_tile above.
      width: 420,
      height: 110,
      data: {
        kind: 'orphan_tray',
        artifacts: mergedOrphans,
        onClick: click,
      },
    } as Node);

    // Edge from root turn (first turn_marker) → tray
    const rootTurn = nodes.find((n) => n.type === 'turn_marker');
    if (rootTurn) {
      edges.push({
        id: 'edge-orphan-tray',
        source: rootTurn.id,
        target: trayId,
        type: 'default',
        style: { strokeDasharray: '2 6', opacity: 0.4 },
      } as Edge);
    }
  }
}

// Flip any `running` node whose turnKey has a canonical assistant
// message to `completed` — defensive backstop for when the canonical
// loop's id-based reconcile missed a stale speculative node (backend
// dropped a tool_call_id, or provider retry minted a different id).
// When the orphan is a tool_call whose id is NOT in the canonical
// message's metadata.tool_calls, log chat.dag.id_drift — that's the
// signal the architect-triad keystone asked us to emit (spec rev 1.1
// § "Identity drift observability").
export function orphanRunningSweep(nodes: Node[], messages: Message[]): void {
  const assistantByUser = new Map<string, Message>();
  let lastUser: string | null = null;
  for (const m of messages) {
    if (m.role === 'user') {
      lastUser = m.id;
    } else if (m.role === 'assistant' && lastUser) {
      assistantByUser.set(lastUser, m);
    }
  }
  if (assistantByUser.size === 0) return;
  for (const n of nodes) {
    const d = n.data as any;
    if (d?.runState === 'running' && d?.turnKey && assistantByUser.has(d.turnKey)) {
      const asst = assistantByUser.get(d.turnKey)!;
      // Drift check: is this a tool_call node whose id is NOT in the
      // canonical tool_calls list? That's the id-drift signal.
      if (n.type === 'tool_call' && n.id.startsWith('tc-')) {
        const specId = n.id.slice(3); // strip 'tc-'
        const canonicalIds = new Set(
          (((asst.metadata as any)?.tool_calls ?? []) as any[])
            .map((c) => c?.identity?.tool_call_id ?? c?.tool_call_id)
            .filter(Boolean),
        );
        if (!canonicalIds.has(specId)) {
          // eslint-disable-next-line no-console
          console.warn('[chat.dag.id_drift]', {
            speculative: n.id,
            turnKey: d.turnKey,
            canonicalIds: Array.from(canonicalIds),
          });
        }
      }
      // eslint-disable-next-line no-console
      console.warn('[chat.dag.orphan_speculative]', {
        id: n.id,
        turnKey: d.turnKey,
      });
      d.runState = 'completed';
    }
  }
}

// When state.status === 'error' for the in-flight turn, flip running
// nodes in that turn to 'error' and emit a synthetic assistant marker
// so the DAG visually closes the turn.
export function errorTurnSweep(nodes: Node[], runtime: RuntimeState): void {
  const errId = runtime.errorTurnId;
  if (!errId) return;
  for (const n of nodes) {
    const d = n.data as any;
    if (d?.turnKey === errId && d?.runState === 'running') {
      d.runState = 'error';
    }
  }
  const existing = nodes.find(
    (n) =>
      n.type === 'turn_marker' &&
      (n.data as any)?.role === 'assistant' &&
      (n.data as any)?.turnKey === errId,
  );
  if (!existing) {
    const userNode = nodes.find((n) => n.id === `user-${errId}`);
    const tIdx = (userNode?.data as any)?.turnIndex ?? 9999;
    nodes.push({
      id: `asst-error-${errId}`,
      type: 'turn_marker',
      data: {
        kind: 'turn_marker',
        role: 'assistant',
        preview: '(error — see chat for details)',
        messageId: `asst-error-${errId}`,
        turnIndex: tIdx,
        isLatestTurn: false,
        turnKey: errId,
        primaryPersona: runtime.primaryPersona ?? null,
      } as TurnMarkerData,
      position: { x: 0, y: 0 },
      // F4 fix: explicit dims bypass RF measurement-gate.
      width: 260,
      height: 120,
    });
  }
}

// When a turn is in flight (user has sent but the assistant
// message hasn't landed), synthesize nodes for the speculative round +
// any live-in-progress tools/peers based on runtime.livePhaseEvents.
// Node IDs are stable (round-<userMessageId>-<round>, tc-<tool_call_id>,
// <parentTcId>-peer-<peer_persona_id>) so the canonical emission in
// the main loop will match-by-id when the assistant message lands.
export function emitSpeculativeTurn(
  nodes: Node[],
  edges: Edge[],
  messages: Message[],
  runtime: RuntimeState,
): void {
  const userMessageId = runtime.inFlightTurnId;
  if (!userMessageId) return;
  const userMsg = messages.find(
    (m) => m.id === userMessageId && m.role === 'user',
  );
  if (!userMsg) return;

  // Skip when a matching assistant has already landed — main loop
  // handled the canonical emission; livePhaseEvents are stale.
  const userIdx = messages.findIndex((m) => m.id === userMessageId);
  const followingAssistant = messages
    .slice(userIdx + 1)
    .find((m) => m.role === 'assistant');
  if (followingAssistant) return;

  // User marker: the main loop already emitted one for every user
  // message in `messages`. If somehow missing (shouldn't happen with
  // current flow), emit a stub.
  let userNode = nodes.find((n) => n.id === `user-${userMessageId}`);
  if (!userNode) {
    userNode = {
      id: `user-${userMessageId}`,
      type: 'turn_marker',
      data: {
        kind: 'turn_marker',
        role: 'user',
        preview: (userMsg.content ?? '').slice(0, 80),
        messageId: userMessageId,
        timestamp: userMsg.timestamp,
        turnIndex: 9999, // sort last; layout sees it above any completed turn
        isLatestTurn: true,
        turnKey: userMessageId,
      } as TurnMarkerData,
      position: { x: 0, y: 0 },
      // F4 fix: explicit dims bypass RF measurement-gate.
      width: 260,
      height: 120,
    };
    nodes.push(userNode);
  }
  const tIdx = (userNode.data as any).turnIndex ?? 9999;

  // Speculative addEdge mirrors the canonical path: 'spine' uses
  // bottom/top handles, 'horizontal' uses right/left. Dedup-guarded
  // so repeated phase events don't pile up edge dupes.
  type EdgeOrient = 'spine' | 'horizontal';
  const addEdge = (
    source: string,
    target: string,
    orient: EdgeOrient = 'spine',
  ) => {
    const id = `${source}->${target}`;
    if (edges.some((e) => e.id === id)) return;
    edges.push({
      id,
      source,
      target,
      sourceHandle: orient === 'spine' ? 'b' : 'r',
      targetHandle: orient === 'spine' ? 't' : 'l',
      type: 'default',
    });
  };

  // Ensure a running round exists; emitRound is idempotent. Rounds
  // chain on the main spine (user → round-1 → round-2 → …) so
  // speculative structure matches the canonical topology one-for-one.
  const roundsEmitted = new Set<number>();
  let prevSpeculativeRoundId: string | null = null;
  const emitRound = (round: number) => {
    if (roundsEmitted.has(round)) return;
    roundsEmitted.add(round);
    const id = `round-${userMessageId}-${round}`;
    // If the canonical main loop already emitted this round, adopt it
    // as the spine cursor without re-emitting.
    if (nodes.some((n) => n.id === id)) {
      prevSpeculativeRoundId = id;
      return;
    }
    nodes.push({
      id,
      type: 'llm_round',
      data: {
        kind: 'llm_round',
        turnId: userMessageId,
        round,
        isSynthesis: false,
        toolCallCount: 0,
        runState: 'running',
        turnIndex: tIdx,
        isLatestTurn: true,
        messageId: userMessageId,
        turnKey: userMessageId,
      } as LlmRoundData,
      position: { x: 0, y: 0 },
      // F4 fix: explicit dims bypass RF measurement-gate.
      width: 220,
      height: 95,
    });
    const spineParent =
      prevSpeculativeRoundId ?? `user-${userMessageId}`;
    addEdge(spineParent, id);
    prevSpeculativeRoundId = id;
  };
  emitRound(1);

  // Walk phase events and update/append speculative nodes.
  for (const ev of runtime.livePhaseEvents) {
    const kind = ev?.kind;
    const details = (ev?.details ?? {}) as Record<string, any>;

    if (kind === 'chat_tool_invoked') {
      const round = Number(details.llm_round ?? 1);
      emitRound(round);
      // Task 14: some LLM providers (DeepSeek, OpenRouter on retry)
      // can hand back a null tool_call_id in the first chunk of a
      // streamed delta. Synthesize a deterministic fallback so the
      // speculative node still appears; log so we know it happened.
      let tcCallId = details.tool_call_id;
      if (!tcCallId) {
        // eslint-disable-next-line no-console
        console.warn('[chat.dag.null_tool_call_id]', {
          userMessageId,
          round,
          tool_name: details.tool_name,
        });
        tcCallId = `fallback-${userMessageId}-${round}-${nodes.length}`;
      }
      const tcId = `tc-${tcCallId}`;
      if (!nodes.some((n) => n.id === tcId)) {
        nodes.push({
          id: tcId,
          type: 'tool_call',
          data: {
            kind: 'tool_call',
            call: {
              identity: {
                tool_call_id: tcCallId,
                tool_name: details.tool_name,
              },
              timing: { llm_round: round, duration_ms: 0 },
              outcome: { status: 'ok' },
            } as any,
            messageId: userMessageId,
            round,
            runState: 'running',
            turnIndex: tIdx,
            isLatestTurn: true,
            turnKey: userMessageId,
          } as ToolCallDagData,
          position: { x: 0, y: 0 },
          // F4 fix: explicit dims bypass RF measurement-gate.
          width: 280,
          height: 90,
        });
        addEdge(`round-${userMessageId}-${round}`, tcId, 'horizontal');
      }
    } else if (
      kind === 'chat_tool_completed' ||
      kind === 'chat_tool_timeout' ||
      kind === 'chat_tool_error' ||
      kind === 'chat_tool_budget_exceeded'
    ) {
      const tcId = `tc-${details.tool_call_id}`;
      const n = nodes.find((nn) => nn.id === tcId);
      if (n) {
        const rawStatus =
          details.status ??
          (kind === 'chat_tool_timeout'
            ? 'timeout'
            : kind === 'chat_tool_error'
              ? 'error'
              : kind === 'chat_tool_budget_exceeded'
                ? 'budget_exceeded'
                : 'ok');
        (n.data as any).runState =
          rawStatus === 'ok' ? 'completed' : rawStatus;
        if (typeof details.duration_ms === 'number') {
          const call = (n.data as any).call;
          if (call?.timing) call.timing.duration_ms = details.duration_ms;
        }
      }
    } else if (kind === 'chat_peer_call_started') {
      const parentId = details.parent_tool_call_id;
      if (!parentId) continue;
      const parentTcId = `tc-${parentId}`;
      const peerPersonaId = details.peer_persona_id;
      const peerId = `${parentTcId}-peer-${peerPersonaId ?? 'unknown'}`;
      if (!nodes.some((n) => n.id === peerId)) {
        nodes.push({
          id: peerId,
          type: 'peer',
          data: {
            kind: 'peer',
            personaName: details.peer_persona_name,
            personaId: peerPersonaId,
            status: 'ok',
            durationMs: 0,
            response: '',
            parentToolCallId: parentId,
            runState: 'running',
            turnIndex: tIdx,
            isLatestTurn: true,
            turnKey: userMessageId,
            messageId: userMessageId,
          } as PeerDagData,
          position: { x: 0, y: 0 },
          // F4 fix: explicit dims bypass RF measurement-gate.
          width: 240,
          height: 140,
        });
        addEdge(parentTcId, peerId, 'horizontal');
      }
    } else if (
      kind === 'chat_peer_call_completed' ||
      kind === 'chat_peer_call_failed' ||
      kind === 'chat_peer_call_timeout'
    ) {
      const parentId = details.parent_tool_call_id;
      const peerPersonaId = details.peer_persona_id;
      const peerId = `tc-${parentId}-peer-${peerPersonaId ?? 'unknown'}`;
      const n = nodes.find((nn) => nn.id === peerId);
      if (n) {
        const rawStatus =
          details.status ??
          (kind === 'chat_peer_call_failed'
            ? 'error'
            : kind === 'chat_peer_call_timeout'
              ? 'timeout'
              : 'ok');
        (n.data as any).runState =
          rawStatus === 'ok' ? 'completed' : rawStatus;
        if (typeof details.duration_ms === 'number') {
          (n.data as any).durationMs = details.duration_ms;
        }
      }
    }
  }
}
